/**
 * Gawura desktop mega nav.
 *
 *  - Top-level <a data-mega-trigger="ID"> reveals matching <div data-mega-panel="ID">
 *  - Inside a panel, <li data-tier3-trigger="ID"> reveals matching <ul data-tier3-panel="ID">
 *
 * All panels are pre-rendered into a single full-width container (#gawuraMega).
 * Visibility is toggled via the `is-visible` class so CSS can transition
 * opacity / visibility. Open + close are both delayed so the panel doesn't
 * flicker when the cursor passes over adjacent menu items.
 */

// ─── DEBUG ────────────────────────────────────────────────────────────────────
// Set to 1, 2, 3 … to force-open that nth top-level mega item and keep it open.
// Set to 0 (or false) for normal behaviour.
const DEBUG_FREEZE_ITEM = 0;
// ─────────────────────────────────────────────────────────────────────────────

const DESKTOP_MQ = '(min-width: 1400px)';
const OPEN_DELAY_MS = 200;
const CLOSE_DELAY_MS = 500;
const TIER3_CLOSE_DELAY_MS = 1000;

export function nav($) {
	initSearchFocus($);
	initMegaNav();
}

function initSearchFocus($) {
	$('#primary-search-link').on('click', function (e) {
		e.preventDefault();
		$('#primary-search-field').trigger('focus');
	});
}

function initMegaNav() {
	const root = document.getElementById('gawuraMega');
	const navList = document.getElementById('primaryNavigation');
	if (!root || !navList) return;

	const triggers = navList.querySelectorAll('[data-mega-trigger]');
	const panels = root.querySelectorAll('[data-mega-panel]');
	if (!triggers.length || !panels.length) return;

	const isDesktop = () => window.matchMedia(DESKTOP_MQ).matches;
	let openTimer = null;
	let closeTimer = null;
	let tier3CloseTimer = null;
	let activeTier3Panel = null; // the panel element whose tier-3 is currently shown
	let activeId = null;

	const cancelOpen = () => {
		if (openTimer !== null) { clearTimeout(openTimer); openTimer = null; }
	};
	const cancelClose = () => {
		if (closeTimer !== null) { clearTimeout(closeTimer); closeTimer = null; }
	};

	const cancelTier3Close = () => {
		if (tier3CloseTimer !== null) { clearTimeout(tier3CloseTimer); tier3CloseTimer = null; }
	};

	// Focus el and mark it as keyboard-navigated so our CSS highlight applies even
	// when the browser doesn't set :focus-visible on programmatic .focus() calls.
	const kbFocus = (el) => {
		el.setAttribute('data-focus-kbd', '');
		el.focus();
		el.addEventListener('blur', () => el.removeAttribute('data-focus-kbd'), { once: true });
	};

	// Returns all keyboard-focusable elements inside container, excluding:
	//  - disabled elements
	//  - elements hidden via visibility:hidden or display:none
	//  - links inside tier-3 panels that are not currently visible
	const getFocusableElements = (container) => {
		const candidates = container.querySelectorAll(
			'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
		);
		return Array.from(candidates).filter((el) => {
			// Skip elements inside hidden tier-3 panels.
			const tier3Panel = el.closest('[data-tier3-panel]');
			if (tier3Panel && !tier3Panel.classList.contains('is-visible')) return false;
			// Skip genuinely invisible elements (checkVisibility is widely supported but has a fallback).
			if (typeof el.checkVisibility === 'function') return el.checkVisibility();
			const style = window.getComputedStyle(el);
			return style.display !== 'none' && style.visibility !== 'hidden';
		});
	};

	// Returns all nav links in this panel ordered depth-first:
	//   title → tier-2-A → [tier-3 children of A] → tier-2-B → [tier-3 children of B] → quick links
	// All tier-3 links are included regardless of current tier-3 visibility, so that
	// Shift+Tab can navigate backward into a tier-3 group that isn't open yet.
	const getDepthFirstElements = (panel) => {
		const result = [];
		const titleLink = panel.querySelector('.gawura-mega__col--title a[href]');
		if (titleLink) result.push(titleLink);
		panel.querySelectorAll('.gawura-mega__col--tier2 li').forEach((li) => {
			const a = li.querySelector('a[href]');
			if (a) result.push(a);
			const tier3Id = li.getAttribute('data-tier3-trigger');
			if (tier3Id) {
				const t3Panel = panel.querySelector(`[data-tier3-panel="${tier3Id}"]`);
				if (t3Panel) t3Panel.querySelectorAll('a[href]').forEach((link) => result.push(link));
			}
		});
		panel.querySelectorAll('.gawura-mega__col--quicklinks a[href]').forEach((a) => result.push(a));
		return result;
	};

	// Focus el and synchronise tier-3 visibility to match its depth-first position in the panel.
	const depthFirstFocus = (panel, el) => {
		const t3Panel = el.closest('[data-tier3-panel]');
		if (t3Panel) {
			setTier3(panel, t3Panel.getAttribute('data-tier3-panel'));
		} else {
			const t3Trigger = el.closest('[data-tier3-trigger]');
			setTier3(panel, t3Trigger ? t3Trigger.getAttribute('data-tier3-trigger') : null);
		}
		kbFocus(el);
	};

	// Returns the data-tier3-panel id that should be visible by default in a panel,
	// based on which tier-2 or tier-3 anchor has class="active" in the rendered markup.
	const getActiveTier3Id = (panel) => {
		// Tier-3 page active: find a.active inside a [data-tier3-panel]
		const activeInTier3 = panel.querySelector('[data-tier3-panel] a.active');
		if (activeInTier3) return activeInTier3.closest('[data-tier3-panel]').getAttribute('data-tier3-panel');
		// Tier-2 page active (and it has children): find [data-tier3-trigger] whose a is active
		const activeTier2 = panel.querySelector('[data-tier3-trigger] a.active');
		if (activeTier2) return activeTier2.closest('[data-tier3-trigger]').getAttribute('data-tier3-trigger');
		return null;
	};

	const setTier3 = (panel, tier3Id) => {
		cancelTier3Close();
		activeTier3Panel = tier3Id ? panel : null;
		panel.querySelectorAll('[data-tier3-panel]').forEach((p) => {
			p.classList.toggle('is-visible', p.getAttribute('data-tier3-panel') === tier3Id);
		});
		panel.querySelectorAll('[data-tier3-trigger]').forEach((li) => {
			li.classList.toggle('is-active', li.getAttribute('data-tier3-trigger') === tier3Id);
		});
	};

	const scheduleTier3Close = (panel) => {
		cancelTier3Close();
		tier3CloseTimer = setTimeout(() => { tier3CloseTimer = null; setTier3(panel, getActiveTier3Id(panel)); }, TIER3_CLOSE_DELAY_MS);
	};

	const showPanel = (id) => {
		if (activeId === id) return;
		activeId = id;

		panels.forEach((p) => {
			const match = p.getAttribute('data-mega-panel') === id;
			p.classList.toggle('is-visible', match);
			if (match) {
				// Default to active tier-3 if one exists, otherwise blank.
				setTier3(p, getActiveTier3Id(p));
			}
		});

		triggers.forEach((t) => {
			t.setAttribute('aria-expanded', t.getAttribute('data-mega-trigger') === id ? 'true' : 'false');
		});

		root.classList.add('is-open');
	};

	const hidePanel = () => {
		// If focus is inside the mega panel, return it to the trigger before hiding
		// so keyboard users are not stranded inside a now-hidden container.
		if (activeId && root.contains(document.activeElement)) {
			const activeTrigger = navList.querySelector(`[data-mega-trigger="${activeId}"]`);
			if (activeTrigger) kbFocus(activeTrigger);
		}
		activeId = null;
		panels.forEach((p) => p.classList.remove('is-visible'));
		triggers.forEach((t) => t.setAttribute('aria-expanded', 'false'));
		root.classList.remove('is-open');
	};

	const scheduleOpen = (id) => {
		cancelClose();
		// If a panel is already open, swap immediately (no flicker — same container fades between contents).
		if (activeId) {
			cancelOpen();
			showPanel(id);
			return;
		}
		cancelOpen();
		openTimer = setTimeout(() => { openTimer = null; showPanel(id); }, OPEN_DELAY_MS);
	};

	const scheduleClose = () => {
		// Don't interrupt keyboard navigation — if focus is inside the nav
		// (trigger list or mega panels), a mouse-leave should not close the panel.
		if (navList.contains(document.activeElement) || root.contains(document.activeElement)) return;
		cancelOpen();
		cancelClose();
		closeTimer = setTimeout(() => { closeTimer = null; hidePanel(); }, CLOSE_DELAY_MS);
	};

	// --- Debug: force-open nth item and disable all hover/close logic -------
	if (DEBUG_FREEZE_ITEM) {
		const target = triggers[DEBUG_FREEZE_ITEM - 1];
		if (target) showPanel(target.getAttribute('data-mega-trigger'));
		return; // skip all event binding
	}

	// --- Top-level triggers --------------------------------------------------
	const triggerArray = Array.from(triggers);
	// All .nav-link elements in order — includes plain links (Stories, Contact Us) with no panel.
	const allNavLinks = Array.from(navList.querySelectorAll('.nav-link'));

	triggerArray.forEach((trigger, triggerIndex) => {
		const id = trigger.getAttribute('data-mega-trigger');
		const li = trigger.closest('li');
		const panel = root.querySelector(`[data-mega-panel="${id}"]`);

		li.addEventListener('mouseenter', () => { if (isDesktop()) scheduleOpen(id); });
		li.addEventListener('mouseleave', () => { if (isDesktop()) scheduleClose(); });
		trigger.addEventListener('focus', () => { if (isDesktop()) scheduleOpen(id); });

		// --- Keyboard Tab routing: depth-first order -------------------------
		// Tab forward from a trigger → first link inside its panel.
		// Shift+Tab from a trigger → last link (depth-first) of the previous panel.
		trigger.addEventListener('keydown', (e) => {
			if (!isDesktop() || e.key !== 'Tab') return;

			if (!e.shiftKey) {
				if (!panel) return;
				const elements = getDepthFirstElements(panel);
				if (!elements.length) return;
				e.preventDefault();
				showPanel(id);
				depthFirstFocus(panel, elements[0]);
			} else {
				const prevIndex = triggerIndex - 1;
				if (prevIndex < 0) return; // let natural Shift+Tab exit the nav
				const prevTrigger = triggerArray[prevIndex];
				const prevId = prevTrigger.getAttribute('data-mega-trigger');
				const prevPanel = root.querySelector(`[data-mega-panel="${prevId}"]`);
				if (!prevPanel) return;
				const elements = getDepthFirstElements(prevPanel);
				if (!elements.length) return;
				e.preventDefault();
				showPanel(prevId);
				depthFirstFocus(prevPanel, elements[elements.length - 1]);
			}
		});

		// --- Keyboard Tab routing: inside the panel (depth-first through tier-3) ----
		// Intercepts every Tab/Shift+Tab within the panel so the order is:
		//   title → tier-2-A → tier-3-A-children → tier-2-B → tier-3-B-children → quick links
		if (panel) {
			panel.addEventListener('keydown', (e) => {
				if (!isDesktop() || e.key !== 'Tab') return;
				const elements = getDepthFirstElements(panel);
				if (!elements.length) return;
				const currentIndex = elements.indexOf(document.activeElement);
				if (currentIndex === -1) return; // element not in our list; let browser handle it

				if (!e.shiftKey) {
					if (currentIndex < elements.length - 1) {
						e.preventDefault();
						depthFirstFocus(panel, elements[currentIndex + 1]);
					} else {
						// Last element in panel → move to the next top-level nav link.
						// allNavLinks (not triggerArray) so we also reach plain links
						// like Stories / Contact us that have no mega panel.
						const navIdx = allNavLinks.indexOf(trigger);
						const nextNavLink = allNavLinks[navIdx + 1];
						if (nextNavLink) {
							e.preventDefault();
							kbFocus(nextNavLink);
							// If the next link has no panel of its own (e.g. Stories),
							// close this mega so its links don't stay focusable and
							// loop the Tab sequence back into the open panel.
							if (!nextNavLink.hasAttribute('data-mega-trigger')) hidePanel();
						}
						// If no next nav link, let Tab flow naturally out of the nav.
					}
				} else {
					if (currentIndex > 0) {
						e.preventDefault();
						depthFirstFocus(panel, elements[currentIndex - 1]);
					} else {
						// First element in panel → back to this panel's trigger.
						e.preventDefault();
						kbFocus(trigger);
					}
				}
			});
		}
	});

	// --- Plain nav links (Stories, Contact Us — no mega panel) ---------------
	// Shift+Tab from the first plain link jumps into the last element of the
	// preceding trigger's panel. Subsequent plain links use natural Shift+Tab.
	allNavLinks.filter((link) => !link.hasAttribute('data-mega-trigger')).forEach((link) => {
		link.addEventListener('keydown', (e) => {
			if (!isDesktop() || e.key !== 'Tab' || !e.shiftKey) return;
			const idx = allNavLinks.indexOf(link);
			const prevLink = allNavLinks[idx - 1];
			if (!prevLink) return;
			const prevId = prevLink.getAttribute('data-mega-trigger');
			if (!prevId) return; // prev is also a plain link; let natural Shift+Tab proceed
			const prevPanel = root.querySelector(`[data-mega-panel="${prevId}"]`);
			if (!prevPanel) return;
			const elements = getDepthFirstElements(prevPanel);
			if (!elements.length) return;
			e.preventDefault();
			showPanel(prevId);
			depthFirstFocus(prevPanel, elements[elements.length - 1]);
		});
	});

	// --- Mega container hover keeps it open ---------------------------------
	root.addEventListener('mouseenter', () => { cancelClose(); cancelOpen(); });
	root.addEventListener('mouseleave', () => { if (isDesktop()) scheduleClose(); });

	// --- Tier-2 → Tier-3 swap (event delegation) ----------------------------
	root.addEventListener('mouseover', (e) => {
		// Entering the tier-3 panel col: cancel any pending close.
		if (e.target.closest('[data-tier3-panel]')) { cancelTier3Close(); return; }
		// Entering a tier-2 item that has children: swap tier-3 immediately.
		const li = e.target.closest('[data-tier3-trigger]');
		if (!li || !root.contains(li)) return;
		const panel = li.closest('[data-mega-panel]');
		if (!panel) return;
		setTier3(panel, li.getAttribute('data-tier3-trigger'));
	});

	// When leaving a tier-2 trigger, schedule tier-3 close (cancelled if pointer enters tier-3 col).
	root.addEventListener('mouseout', (e) => {
		const li = e.target.closest('[data-tier3-trigger]');
		if (!li) return;
		if (li.contains(e.relatedTarget)) return;
		const panel = li.closest('[data-mega-panel]');
		if (!panel) return;
		scheduleTier3Close(panel);
	});

	// Leaving the tier-3 col: schedule close.
	root.addEventListener('mouseout', (e) => {
		const col = e.target.closest('[data-tier3-panel]');
		if (!col) return;
		if (col.contains(e.relatedTarget)) return;
		const panel = col.closest('[data-mega-panel]');
		if (!panel) return;
		scheduleTier3Close(panel);
	});

	root.addEventListener('focusin', (e) => {
		const li = e.target.closest('[data-tier3-trigger]');
		if (!li) return;
		const panel = li.closest('[data-mega-panel]');
		if (!panel) return;
		setTier3(panel, li.getAttribute('data-tier3-trigger'));
	});

	// --- Close when focus leaves both the nav list and the mega container ---
	[navList, root].forEach((container) => {
		container.addEventListener('focusout', (e) => {
			if (!isDesktop() || !activeId) return;
			const going = e.relatedTarget;
			if (!going || (!navList.contains(going) && !root.contains(going))) scheduleClose();
		});
	});

	// --- Escape + outside click close ---------------------------------------
	document.addEventListener('keydown', (e) => {
		if (e.key === 'Escape' && activeId) { cancelOpen(); hidePanel(); }
	});

	document.addEventListener('click', (e) => {
		if (!activeId) return;
		if (root.contains(e.target) || navList.contains(e.target)) return;
		cancelOpen();
		hidePanel();
	});
}
